import { CompoundComponentContext, useCompoundParent } from '@mui/base/useCompound';
import { Root as AccordionRoot } from '@radix-ui/react-accordion';
import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import AccordionGroupContext from './accordion-group-context';

import type { AccordionMultipleProps, AccordionSingleProps } from '@radix-ui/react-accordion';
import type { ForwardedRef, ReactElement } from 'react';

import type {
  AccordionGroupComponentType,
  AccordionGroupContextProps,
  AccordionGroupProps,
  AccordionMetadata,
} from './accordion-group.types';

import './accordion-group.scss';

/**
 * The accordion component is a multi-usage component allowing to display various types of content,
 * in collapsible sections that can be easily scanned and save screen space.
 *
 * The `AccordionGroup` component serves as a container for multiple `Accordion` components.
 * It ensures that the accordions are grouped together, providing a unified structure and styling for the accordion set.
 *
 * ### Usage
 * Wrap the `AccordionGroup` component around multiple `Accordion` components to group them together.
 *
 * ```tsx
 * import { AccordionGroup, Accordion, AccordionHeader, AccordionContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <AccordionGroup>
 *       <Accordion value="banana-facts">
 *         <AccordionHeader>Banana facts</AccordionHeader>
 *         <AccordionContent>
 *           Bananas are a great source of potassium and can help replenish electrolytes after exercise.
 *         </AccordionContent>
 *       </Accordion>
 *       <Accordion>...</Accordion>
 *       <Accordion>...</Accordion>
 *     </AccordionGroup>
 *   );
 * }
 * ```
 */
const AccordionGroup = forwardRef(
  <Multiple extends boolean = false>(
    props: AccordionGroupProps<Multiple>,
    forwardedRef: ForwardedRef<HTMLDivElement> | null,
  ): ReactElement => {
    const {
      children,
      className,
      defaultValue,
      disableDivider = false,
      multiple = false,
      nonCollapsible = false,
      onValueChange,
      styling = 'plain',
      value,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const { contextValue: compoundComponentContextValue } = useCompoundParent<number | string, AccordionMetadata>();

    const contextValue = useMemo<AccordionGroupContextProps>(
      () => ({ disableDivider, styling }),
      [disableDivider, styling],
    );

    const singleComponentProps: AccordionSingleProps = {
      collapsible: !nonCollapsible,
      defaultValue: defaultValue as AccordionGroupProps<false>['defaultValue'],
      onValueChange: onValueChange as AccordionGroupProps<false>['onValueChange'],
      type: 'single',
      value: value as AccordionGroupProps<false>['value'],
    };

    const multipleComponentProps: AccordionMultipleProps = {
      defaultValue: defaultValue as AccordionGroupProps<true>['defaultValue'],
      onValueChange: onValueChange as AccordionGroupProps<true>['onValueChange'],
      type: 'multiple',
      value: value as AccordionGroupProps<true>['value'],
    };

    const classes = cn(
      'rbui-accordion-group',
      {
        'rbui-accordion-group--styled': styling === 'styled',
      },
      className,
    );

    return (
      <CompoundComponentContext.Provider value={compoundComponentContextValue}>
        <AccordionGroupContext.Provider value={contextValue}>
          <AccordionRoot
            {...(multiple ? multipleComponentProps : singleComponentProps)}
            {...rest}
            className={classes}
            ref={forwardedRef}
          >
            {children}
          </AccordionRoot>
        </AccordionGroupContext.Provider>
      </CompoundComponentContext.Provider>
    );
  },
);

AccordionGroup.displayName = 'AccordionGroup';

export default AccordionGroup as AccordionGroupComponentType;
